import CustomStore from "devextreme/data/custom_store";

import jwtService from "@service/jwt-service";
import { CommonUtil } from "@utils/CommonUtil";
import { UserConst } from "@constants/user-const";
import { CommonConst } from "@constants/common-const";
import DataSource from "devextreme/data/data_source";
import { commonNotify } from "@utils/common";

const Axios = jwtService.axiosIns;

type CustomerDataSource = {
  data: Customer[];
  totalCount: number;
};

//----------------------------------
// 사용자 - 전체 고객 사용자 (운영자 기능)
//----------------------------------

// 고객 사용자 목록 조회
const getCustomerUsers = (params: any) => {
  return Axios.get(`/users/customerusers/?${params}`);
};

// 고객 사용자 조회
const _getCustomerUser = (username: any) => {
  return Axios.get(`/users/customerusers/${username}`);
};

// 고객 사용자 검색
const getSearchedCustomerUsers = (params: any) => {
  return Axios.get(`/users/customerusers/search?${params}`);
};

// 고객 사용자 등록
const createCustomerUser = (...args: any[]) => {
  return Axios.post("/users/customerusers", ...args);
};

// 고객 사용자 및 고객 등록
const createCustomerUserAndCustomer = (...args: any[]) => {
  return Axios.post("/users/customerusers/customer", ...args);
};

// 고객 사용자 수정
const updateCustomerUser = (username: any, ...args: any[]) => {
  return Axios.put(`/users/customerusers/${username}`, ...args);
};

// 고객 사용자 비밀번호 수정
const updateCustomerUserPassword = (...args: any[]) => {
  return Axios.put("/users/customerusers/password", ...args);
};

// 고객 사용자 삭제
const _deleteCustomerUser = (username: any) => {
  return Axios.delete(`/users/customerusers/${username}`);
};

// 고객 목록 검색 조회
export async function getCustomerSearch(params: any) {
  return Axios.get<CustomerDataSource>(`/customers/search?${params}`);
}

// 고객 목록 검색 조회 - 조직별(운영자)
export async function getCompanyCustomerSearch(companyId: number, params: any) {
  return Axios.get<CustomerDataSource>(`/companies/${companyId}/customers/search?${params}`);
}

// 고객 조회
async function getCustomer(id: number) {
  return Axios.get<CustomerDataSource>(`/customers/${id}`);
}

// 고객 조회 - 조직별(운영자)
async function getCompanyCustomer(companyId: number, id: number) {
  return Axios.get<CustomerDataSource>(`/companies/${companyId}/customers/${id}`);
}

//----------------------------------
// 사용자 - 전체 고객 사용자 (운영자 기능)
//----------------------------------

/**
 * 고객 사용자 목록 조회(Dx)
 */
export function getCustomerDataSource() {
  return new CustomStore({
    key: "id",
    onLoading() {
      // store.commit("store/setLoading", true);
    },
    onLoaded() {
      // store.commit("store/setLoading", false);
    },
    load(loadOptions) {
      // 파라미터 생성
      const params = CommonUtil.makeDxDataGridParams(loadOptions);
      return getCustomerUsers(params)
        .then((response: any) => ({
          data: response.data.data,
          totalCount: response.data.totalCount,
        }))
        .catch(() => {
          // store.commit("store/setLoading", false);
          throw new Error("Data Loading Error");
        });
    },
  });
}

/**
 * 고객 사용자 조회
 * @param username 고객 사용자 아이디
 */
export async function getCustomerUser(username: string) {
  let result = null;
  let customerUser = null;

  // store.commit("store/setLoading", true);
  // store.commit("store/setLoadMessage", "잠시만 기다려주세요."); // 불러오기

  try {
    result = await _getCustomerUser(username);

    customerUser = result.data;
    customerUser.roles = result.data.roles;
  } catch (e) {
    commonNotify(CommonConst.errorGet, "error");
    throw e;
  } finally {
    // store.commit("store/setLoading", false);
    // store.commit("store/setLoadMessage", "잠시만 기다려주세요.");
  }

  return customerUser;
}

/**
 * 고객사용자 등록/수정
 * @param modifyType 수정형태
 * @param username 고객사용자 아이디
 * @param customerUser 고객사용자 객체
 */
export async function saveCustomerUser(
  modifyType: string = null,
  username: string = null,
  customerUser: CustomerUser = null,
) {
  // store.commit("store/setLoading", true);
  // store.commit("store/setLoadMessage", "잠시만 기다려주세요."); // 저장

  // JSON화
  const jsonData: string = JSON.stringify(customerUser);

  // 통신
  try {
    if (modifyType === CommonConst.MODIFY_TYPE_CREATE) {
      // [등록]
      await createCustomerUser(jsonData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      commonNotify(CommonConst.completeSave);
    } else {
      // [수정]
      await updateCustomerUser(username, jsonData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      commonNotify(CommonConst.completeUpdate);
    }
  } catch (e) {
    commonNotify(CommonConst.errorSave, "error");
    throw e;
  } finally {
    // store.commit("store/setLoading", false);
    // store.commit("store/setLoadMessage", "잠시만 기다려주세요.");
  }
}

/**
 * 고객사용자와 고객 등록/수정
 * @param modifyType 수정형태
 * @param username 고객사용자 아이디
 * @param customerUser 고객사용자 객체
 * @return 고객 사용자
 */
export async function saveCustomerUserAndCustomer(
  modifyType: string = null,
  username: string = null,
  customerUser: CustomerUser = null,
) {
  // store.commit("store/setLoading", true);
  // store.commit("store/setLoadMessage", "잠시만 기다려주세요."); // 저장
  let result = null;
  let newCustomerUser: any = null;

  // JSON화
  const jsonData: string = JSON.stringify(customerUser);

  // 통신
  try {
    if (modifyType === CommonConst.MODIFY_TYPE_CREATE) {
      // [등록]
      result = await createCustomerUserAndCustomer(jsonData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      newCustomerUser = result.data;

      commonNotify(CommonConst.completeSave);
    } else {
      // [수정]
      result = await updateCustomerUser(username, jsonData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      newCustomerUser = result.data;

      commonNotify(CommonConst.completeUpdate);
    }
  } catch (e) {
    commonNotify(CommonConst.errorSave, "error");
    throw e;
  } finally {
    // store.commit("store/setLoading", false);
    // store.commit("store/setLoadMessage", "잠시만 기다려주세요.");
  }
  return newCustomerUser;
}

/**
 * 고객 사용자 비밀번호 변경
 */
export async function customerUpdatePassword(
  updatedUser: CustomerUser = null,
  tempPassword = "",
  tempPasswordConfirm = "",
) {
  // store.commit("store/setLoading", true);
  // store.commit("store/setLoadMessage", "잠시만 기다려주세요."); // 불러오기

  // JSON화
  const jsonData: string = JSON.stringify({
    ...updatedUser,
    password: tempPassword,
    passwordConfirm: tempPasswordConfirm,
  });

  // 수정
  try {
    // 조직원 비밀번호 수정
    await updateCustomerUserPassword(jsonData, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    commonNotify(CommonConst.completeUpdate);
  } catch (e) {
    commonNotify(CommonConst.errorSave, "error");
    throw e;
  } finally {
    // store.commit("store/setLoading", false);
    // store.commit("store/setLoadMessage", "잠시만 기다려주세요.");
  }
}

/**
 *  고객 사용자 삭제
 */
export async function deleteCustomerUser(username: string) {
  // store.commit("store/setLoading", true);
  // 통신
  await _deleteCustomerUser(username)
    .then((response: any) => {
      commonNotify(CommonConst.completeDelete);
    })
    .catch((e: any) => {
      commonNotify(CommonConst.errorGet, "error");
      throw e;
    })
    .finally(() => {
      // store.commit("store/setLoading", false);
    });
}

/**
 * 저장전 처리
 */
export function beforeSaveCustomer(updateCustomerUser: CustomerUser) {
  const customerUser = updateCustomerUser;
  if (customerUser.customerType === UserConst.CUSTOMER_TYPE_PERSONAL) {
    customerUser.companyName = null;
    customerUser.ceoName = null;
    customerUser.businessType = null;
    customerUser.businessTypeDetail = null;
  } else {
    customerUser.gender = null;
    customerUser.birthdate = null;
  }

  // 농업인경영체가 미등록일경우, 농업인경영체번호 제거
  if (!customerUser.agriRegistered) {
    customerUser.agriRegNumber = null;
  }

  return customerUser;
}

/**
 * 고객 목록 검색 조회(Dx) <- TODO DxUI 사용하여 계약등록/수정, 설계상세, 설계수정 모두 하나로 통일하기 + accessType, companyId 활용하고 jwtService URI 사용하기
 */
export function getCustomerSearchDataSource(accessType: string, companyId: number) {
  let response: any;
  return new DataSource({
    paginate: false,
    store: new CustomStore({
      key: "id",
      loadMode: "processed",
      async load(loadOptions: any) {
        let params = `search=${loadOptions.searchValue}`;
        if (accessType === CommonConst.ACCESS_TYPE_COMPANY) {
          response = await getCompanyCustomerSearch(companyId, params);
        } else if (accessType === CommonConst.ACCESS_TYPE_MY) {
          response = await getCustomerSearch(params);
        }
        return response.data;
      },
      async byKey(key) {
        if (accessType === CommonConst.ACCESS_TYPE_COMPANY) {
          response = await getCompanyCustomer(companyId, key);
        } else if (accessType === CommonConst.ACCESS_TYPE_MY) {
          response = await getCustomer(key);
        }
        return response.data;
      },
      cacheRawData: false,
      useDefaultSearch: false,
    }),
  });
}
